import { InstantAnswerContext } from "@faire/help-center-self-serve/types";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalOrders from "@faire/web-api--source/routes/brand-portal/orders";
import brandPortalOrdersbrandOrderTokenEdit from "@faire/web-api--source/routes/brand-portal/orders/brandOrderToken/edit";
import React, { type JSX } from "react";

import { Bold } from "../common/Bold";

interface EditOrderInformationIAProps {
  context: InstantAnswerContext;
}

export const EditOrderInformationIA: React.FC<EditOrderInformationIAProps> = ({
  context,
}) => {
  const brandOrderToken = context.brandOrder?.brand_order?.token;
  if (brandOrderToken) {
    return (
      <Column>
        <Typography>
          <StrictLocalMsg
            defaultMessage="To edit order information, first go to the <OrderDetailsLinkFormatting>Order details page</OrderDetailsLinkFormatting>. Then select the <LinkFormatting>Edit</LinkFormatting> link to make changes to shipping details, promotions, or products."
            values={{
              LinkFormatting: (chunks: JSX.Element) =>
                EditOrderLinkFormatting(chunks, brandOrderToken),
              OrderDetailsLinkFormatting: (chunks: JSX.Element) =>
                OrderLinkFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/0fbb053871997208eb7925ae63d9fb541c7fcfe0f42c5bd0c474a140fa565f7a.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="You can edit the shipping address or ship date by clicking the pen icon next to the respective sections. After making your edits, click <BoldFormatting>Update</BoldFormatting> to save them."
            values={{
              BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/0fbb053871997208eb7925ae63d9fb541c7fcfe0f42c5bd0c474a140fa565f7a.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="You can update product quantities by editing the <BoldFormatting>Qty</BoldFormatting> fields. You also apply or remove a promotion from the order as well."
            values={{
              BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/0fbb053871997208eb7925ae63d9fb541c7fcfe0f42c5bd0c474a140fa565f7a.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="When you make changes, you’ll have the option to leave a note for the retailer, which we highly recommend for a positive customer experience. The retailer will receive an email notification detailing the changes made."
            values={{
              BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/0fbb053871997208eb7925ae63d9fb541c7fcfe0f42c5bd0c474a140fa565f7a.png",
            }}
          />
        </Typography>
      </Column>
    );
  } else {
    return (
      <Column>
        <Typography>
          <StrictLocalMsg
            defaultMessage="To edit order information for a new or unfulfilled order, first go to your <LinkFormatting>Orders</LinkFormatting>, and navigate to the <BoldFormatting>order detail</BoldFormatting> page for the order you’d like to change. From there, select the <BoldFormatting>Edit</BoldFormatting> link to make changes to shipping details, promotions, or products. "
            values={{
              BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              LinkFormatting: (chunks: JSX.Element) =>
                OrderLinkFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/754258778c17b49c75f71678ec66f2801abec94c733de942db56f1c58e27031c.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="You can edit the shipping address or ship date by clicking the pen icon next to the respective sections. After making your edits, click <BoldFormatting>Update</BoldFormatting> to save them."
            values={{
              BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/754258778c17b49c75f71678ec66f2801abec94c733de942db56f1c58e27031c.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="You can update product quantities by editing the <BoldFormatting>Qty</BoldFormatting> fields. You also apply or remove a promotion from the order as well."
            values={{
              BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/754258778c17b49c75f71678ec66f2801abec94c733de942db56f1c58e27031c.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="When you make changes, you’ll have the option to leave a note for the retailer, which we highly recommend for a positive customer experience. The retailer will receive an email notification detailing the changes made."
            values={{
              BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/754258778c17b49c75f71678ec66f2801abec94c733de942db56f1c58e27031c.png",
            }}
          />
        </Typography>
      </Column>
    );
  }
};

const EditOrderLinkFormatting = (
  chunks: JSX.Element,
  brandOrderToken: string
) => (
  <Button
    variant="tertiary"
    linkTo={brandPortalOrdersbrandOrderTokenEdit(brandOrderToken)}
    target="_blank"
  >
    <Bold>{chunks}</Bold>
  </Button>
);

const OrderLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalOrders()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
